import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiSun, FiMoon, FiChevronRight, FiVideo, FiDownload } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import AnimatedLogo from './AnimatedLogo';
import './Navbar.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [imageToolsOpen, setImageToolsOpen] = useState(false);
  const [pdfToolsOpen, setPdfToolsOpen] = useState(false);
  const [videoToolsOpen, setVideoToolsOpen] = useState(false);
  const [converterSubOpen, setConverterSubOpen] = useState(false);
  const [mobileImageToolsOpen, setMobileImageToolsOpen] = useState(false);
  const [mobilePdfToolsOpen, setMobilePdfToolsOpen] = useState(false);
  const [mobileVideoToolsOpen, setMobileVideoToolsOpen] = useState(false);
  const [mobileConverterSubOpen, setMobileConverterSubOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const converterFormats = [
    { title: 'JPG → PNG', from: 'JPG', to: 'PNG' },
    { title: 'JPG → WebP', from: 'JPG', to: 'WebP' },
    { title: 'PNG → WebP', from: 'PNG', to: 'WebP' },
    { title: 'AVIF → WebP', from: 'AVIF', to: 'WebP' }
  ];

  const videoTools = [
    { title: 'Video to GIF', path: '/video-to-gif' },
    { title: 'Video to Audio', path: '/video-to-audio' },
    { title: 'Remove Audio', path: '/video-audio-remover' },
    { title: 'Compress Video', path: '/video-compressor' }
  ];

  const pdfTools = [
    { title: 'Merge PDF', path: '/pdf-merge' },
    { title: 'Split PDF', path: '/pdf-split' },
    { title: 'PDF to Images', path: '/pdf-to-images' },
    { title: 'Add Watermark', path: '/pdf-watermark' },
    { title: 'Organize Pages', path: '/pdf-organize' },
    { title: 'Compress PDF', path: '/compress-pdf' },
    { title: 'Text to PDF', path: '/text-to-pdf' }
  ];

  const navLinks = [
    { path: '/', label: 'Home' }
  ];

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleConverterClick = (from, to) => {
    navigate(`/image-converter?from=${from}&to=${to}`);
    setImageToolsOpen(false);
    setConverterSubOpen(false);
  };

  const handleImageReducerClick = () => {
    navigate('/image-compressor');
    setImageToolsOpen(false);
    setIsOpen(false);
  };

  const handleImageCropperClick = () => {
    navigate('/image-cropper');
    setImageToolsOpen(false);
    setIsOpen(false);
  };

  const handleBase64ConverterClick = () => {
    navigate('/base64-converter');
    setImageToolsOpen(false);
    setIsOpen(false);
  };

  const handleViewAllClick = () => {
    navigate('/image-tools');
    setImageToolsOpen(false);
    setConverterSubOpen(false);
    setIsOpen(false);
  };

  const handlePdfToolClick = (path) => {
    navigate(path);
    setPdfToolsOpen(false);
    setIsOpen(false);
  };

  const handleViewAllPdfClick = () => {
    navigate('/pdf-tools');
    setPdfToolsOpen(false);
    setIsOpen(false);
  };

  const handleVideoToolClick = (path) => {
    navigate(path);
    setVideoToolsOpen(false);
    setIsOpen(false);
  };

  const handleViewAllVideoClick = () => {
    navigate('/video-tools');
    setVideoToolsOpen(false);
    setIsOpen(false);
  };

  const handleMobileConverterClick = (from, to) => {
    navigate(`/image-converter?from=${from}&to=${to}`);
    setIsOpen(false);
    setMobileImageToolsOpen(false);
    setMobileConverterSubOpen(false);
  };

  return (
    <motion.nav 
      className={`navbar ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <AnimatedLogo size={48} />
          <span className="logo-text">
            <span className="logo-text-white">Multiverse</span>
            <span className="logo-text-gradient">Tools</span>
          </span>
        </Link>

        <div className="navbar-desktop">
          <div className="nav-links">
            {/* Home - First Item */}
            <Link
              to="/"
              className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
            >
              Home
              {location.pathname === '/' && (
                <motion.div
                  className="active-indicator"
                  layoutId="activeIndicator"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </Link>

            {/* Image Tools Dropdown - Second Item */}
            <div 
              className="nav-link-dropdown"
              onMouseEnter={() => setImageToolsOpen(true)}
              onMouseLeave={() => {
                setImageToolsOpen(false);
                setConverterSubOpen(false);
              }}
            >
              <Link 
                to="/image-tools" 
                className={`nav-link ${location.pathname === '/image-tools' ? 'active' : ''}`}
                onClick={() => {
                  setImageToolsOpen(false);
                  setConverterSubOpen(false);
                }}
              >
                Image Tools
                {location.pathname === '/image-tools' && (
                  <motion.div
                    className="active-indicator"
                    layoutId="activeIndicator"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>

              <AnimatePresence>
                {imageToolsOpen && (
                  <motion.div
                    className="dropdown-menu"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Image Converter with Sub-dropdown */}
                    <div 
                      className="dropdown-item-wrapper"
                      onMouseEnter={() => setConverterSubOpen(true)}
                      onMouseLeave={() => setConverterSubOpen(false)}
                    >
                      <div className="dropdown-item has-submenu">
                        <span>Image Converter</span>
                        <FiChevronRight className="chevron-icon" />
                      </div>

                      <AnimatePresence>
                        {converterSubOpen && (
                          <motion.div
                            className="sub-dropdown-menu"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            {converterFormats.map((format, index) => (
                              <div
                                key={index}
                                className="sub-dropdown-item"
                                onClick={() => handleConverterClick(format.from, format.to)}
                              >
                                {format.title}
                              </div>
                            ))}
                            <div className="sub-dropdown-divider" />
                            <div
                              className="sub-dropdown-item view-all"
                              onClick={handleViewAllClick}
                            >
                              View All
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Image Size Reducer */}
                    <div 
                      className="dropdown-item"
                      onClick={handleImageReducerClick}
                    >
                      <span>Image Size Reducer</span>
                    </div>

                    {/* Image Cropper */}
                    <div 
                      className="dropdown-item"
                      onClick={handleImageCropperClick}
                    >
                      <span>Image Cropper</span>
                    </div>

                    {/* Base64 Converter */}
                    <div 
                      className="dropdown-item"
                      onClick={handleBase64ConverterClick}
                    >
                      <span>Base64 Converter</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* PDF Tools Dropdown - Third Item */}
            <div 
              className="nav-link-dropdown"
              onMouseEnter={() => setPdfToolsOpen(true)}
              onMouseLeave={() => setPdfToolsOpen(false)}
            >
              <Link 
                to="/pdf-tools" 
                className={`nav-link ${location.pathname === '/pdf-tools' ? 'active' : ''}`}
                onClick={() => setPdfToolsOpen(false)}
              >
                PDF Tools
                {location.pathname === '/pdf-tools' && (
                  <motion.div
                    className="active-indicator"
                    layoutId="activeIndicator"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>

              <AnimatePresence>
                {pdfToolsOpen && (
                  <motion.div
                    className="dropdown-menu"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {pdfTools.map((tool, index) => (
                      <div
                        key={index}
                        className="dropdown-item"
                        onClick={() => handlePdfToolClick(tool.path)}
                      >
                        <span>{tool.title}</span>
                      </div>
                    ))}
                    <div className="dropdown-divider" />
                    <div
                      className="dropdown-item view-all"
                      onClick={handleViewAllPdfClick}
                    >
                      View All PDF Tools
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Video Tools Dropdown - Fourth Item */}
            <div 
              className="nav-link-dropdown"
              onMouseEnter={() => setVideoToolsOpen(true)}
              onMouseLeave={() => setVideoToolsOpen(false)}
            >
              <Link 
                to="/video-tools" 
                className={`nav-link ${location.pathname === '/video-tools' ? 'active' : ''}`}
                onClick={() => setVideoToolsOpen(false)}
              >
                Video Tools
                {location.pathname === '/video-tools' && (
                  <motion.div
                    className="active-indicator"
                    layoutId="activeIndicator"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>

              <AnimatePresence>
                {videoToolsOpen && (
                  <motion.div
                    className="dropdown-menu"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {videoTools.map((tool, index) => (
                      <div
                        key={index}
                        className="dropdown-item"
                        onClick={() => handleVideoToolClick(tool.path)}
                      >
                        <span>{tool.title}</span>
                      </div>
                    ))}
                    <div className="dropdown-divider" />
                    <div
                      className="dropdown-item view-all"
                      onClick={handleViewAllVideoClick}
                    >
                      View All Video Tools
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Other Nav Links */}
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <motion.div
                    className="active-indicator"
                    layoutId="activeIndicator"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </div>

          <div className="navbar-actions">
            <motion.button 
              className="icon-btn theme-toggle"
              onClick={toggleTheme}
              whileHover={{ scale: 1.05, rotate: 180 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              {theme === 'dark' ? <FiSun /> : <FiMoon />}
            </motion.button>

            <motion.a
              href="https://play.google.com/store/apps/details?id=com.instasaver.codemultiverse"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-signup"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiDownload />
              <span>Get Insta Saver App</span>
            </motion.a>
          </div>
        </div>

        <div className="navbar-mobile-actions">
          <motion.button 
            className="icon-btn theme-toggle"
            onClick={toggleTheme}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </motion.button>
          
          <motion.button 
            className="menu-toggle"
            onClick={toggleMenu}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isOpen ? <FiX /> : <FiMenu />}
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mobile-menu-content">
              {/* Home - First Item */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0 }}
              >
                <Link
                  to="/"
                  className={`mobile-nav-link ${location.pathname === '/' ? 'active' : ''}`}
                  onClick={() => setIsOpen(false)}
                >
                  Home
                </Link>
              </motion.div>

              {/* Mobile Image Tools Dropdown - Second Item */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div
                  className={`mobile-nav-link ${location.pathname === '/image-tools' ? 'active' : ''}`}
                  onClick={() => setMobileImageToolsOpen(!mobileImageToolsOpen)}
                >
                  <span>Image Tools</span>
                  <FiChevronRight className={`mobile-chevron ${mobileImageToolsOpen ? 'open' : ''}`} />
                </div>

                <AnimatePresence>
                  {mobileImageToolsOpen && (
                    <motion.div
                      className="mobile-sub-menu"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {/* Mobile Image Converter with Sub-dropdown */}
                      <div
                        className="mobile-sub-item"
                        onClick={() => setMobileConverterSubOpen(!mobileConverterSubOpen)}
                      >
                        <span>Image Converter</span>
                        <FiChevronRight className={`mobile-chevron ${mobileConverterSubOpen ? 'open' : ''}`} />
                      </div>

                      <AnimatePresence>
                        {mobileConverterSubOpen && (
                          <motion.div
                            className="mobile-sub-sub-menu"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            {converterFormats.map((format, index) => (
                              <div
                                key={index}
                                className="mobile-sub-sub-item"
                                onClick={() => handleMobileConverterClick(format.from, format.to)}
                              >
                                {format.title}
                              </div>
                            ))}
                            <div className="mobile-sub-divider" />
                            <div
                              className="mobile-sub-sub-item view-all"
                              onClick={handleViewAllClick}
                            >
                              View All
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Mobile Image Size Reducer */}
                      <div
                        className="mobile-sub-item"
                        onClick={handleImageReducerClick}
                      >
                        <span>Image Size Reducer</span>
                      </div>

                      {/* Mobile Image Cropper */}
                      <div
                        className="mobile-sub-item"
                        onClick={handleImageCropperClick}
                      >
                        <span>Image Cropper</span>
                      </div>

                      {/* Mobile Base64 Converter */}
                      <div
                        className="mobile-sub-item"
                        onClick={handleBase64ConverterClick}
                      >
                        <span>Base64 Converter</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Mobile PDF Tools Dropdown - Third Item */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div
                  className={`mobile-nav-link ${location.pathname === '/pdf-tools' ? 'active' : ''}`}
                  onClick={() => setMobilePdfToolsOpen(!mobilePdfToolsOpen)}
                >
                  <span>PDF Tools</span>
                  <FiChevronRight className={`mobile-chevron ${mobilePdfToolsOpen ? 'open' : ''}`} />
                </div>

                <AnimatePresence>
                  {mobilePdfToolsOpen && (
                    <motion.div
                      className="mobile-sub-menu"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {pdfTools.map((tool, index) => (
                        <div
                          key={index}
                          className="mobile-sub-item"
                          onClick={() => handlePdfToolClick(tool.path)}
                        >
                          <span>{tool.title}</span>
                        </div>
                      ))}
                      <div className="mobile-sub-divider" />
                      <div
                        className="mobile-sub-item view-all"
                        onClick={handleViewAllPdfClick}
                      >
                        View All PDF Tools
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Mobile Video Tools Dropdown - Fourth Item */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div
                  className={`mobile-nav-link ${location.pathname === '/video-tools' ? 'active' : ''}`}
                  onClick={() => setMobileVideoToolsOpen(!mobileVideoToolsOpen)}
                >
                  <span>Video Tools</span>
                  <FiChevronRight className={`mobile-chevron ${mobileVideoToolsOpen ? 'open' : ''}`} />
                </div>

                <AnimatePresence>
                  {mobileVideoToolsOpen && (
                    <motion.div
                      className="mobile-sub-menu"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {videoTools.map((tool, index) => (
                        <div
                          key={index}
                          className="mobile-sub-item"
                          onClick={() => handleVideoToolClick(tool.path)}
                        >
                          <span>{tool.title}</span>
                        </div>
                      ))}
                      <div className="mobile-sub-divider" />
                      <div
                        className="mobile-sub-item view-all"
                        onClick={handleViewAllVideoClick}
                      >
                        View All Video Tools
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Other Nav Links */}
              {navLinks.slice(1).map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (index + 3) * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className={`mobile-nav-link ${location.pathname === link.path ? 'active' : ''}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}

              <motion.a
                href="https://play.google.com/store/apps/details?id=com.instasaver.codemultiverse"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-signup mobile"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <FiDownload />
                <span>Get Insta Saver App</span>
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
